import { useCallback } from 'react';
import type { Options } from 'react-use-websocket';
import useWebSocket from 'react-use-websocket';
import { useAuth0 } from '@auth0/auth0-react';
import { WEBSOCKET_MESSAGES } from '@server/websocket-messages';
import { useDispatch } from 'react-redux';
import useSocketHandlers from './useSocketHandlers';
import { telemetry } from '../telemetry';

type Message = {
  msgName: string;
  payload: any;
};

const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_URL;

const useWebSockets = () => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const {
    fetchCompletedHandler,
    slackConnectionCompletedHandler,
    slackDisconnectionCompletedHandler,
    teamsConnectionCompletedHandler,
    teamsDisconnectionCompletedHandler,
    reportingActivationCompletedHandler,
    notificationsNewHandler,
    sfChangeFeedNotificationReceived,
    automationsDeployCompletedHandler,
    automationsDeleteCompletedHandler,
    rollupsDeployCompletedHandler,
    devOpsCenterElementsDeployCompletedHandler,
    deploymentCompletedHandler,
    parseCompletedHandler,
    refreshHomepageDataHandler,
    fieldsUtilizationCompletedHandler,
    issuesScanUpdateHandler,
    agentFileStatusUpdatedHandler,
    chatNameUpdatedHandler,
    teamsInstallCompletedHandler,
    teamsUninstallCompletedHandler,
    aiProcessMiningFunnelMapCompletedHandler,
    aiProcessMiningAgentFunnelMapCompletedHandler,
    researchStartedHandler,
    researchProgressHandler,
    researchCompletedHandler,
    simulationCompletedHandler,
  } = useSocketHandlers();

  const onMessage = async (event: MessageEvent) => {
    const msgJson = await event.data;
    if (msgJson === 'pong') {
      return;
    }
    const msg: Message = JSON.parse(msgJson);

    const { msgName, payload } = msg;
    switch (msgName) {
      case WEBSOCKET_MESSAGES.FETCH_COMPLETED: {
        fetchCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.DATA_INTEGRATION_SLACK_CONNECTION_COMPLETED: {
        slackConnectionCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.DATA_INTEGRATION_SLACK_DISCONNECTION_COMPLETED: {
        slackDisconnectionCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.REPORTING_ACTIVATION_COMPLETED: {
        reportingActivationCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.NOTIFICATIONS_REFETCH: {
        notificationsNewHandler();
        break;
      }
      case WEBSOCKET_MESSAGES.NEW_CHANGE_FEED_LIST_ITEMS: {
        sfChangeFeedNotificationReceived();
        break;
      }
      case WEBSOCKET_MESSAGES.AUTOMATIONS_TOGGLE_ACTIVATION_COMPLETED:
      case WEBSOCKET_MESSAGES.AUTOMATIONS_DEPLOY_COMPLETED: {
        automationsDeployCompletedHandler(payload, msgName);
        break;
      }
      case WEBSOCKET_MESSAGES.ROLLUPS_DEPLOY_COMPLETED: {
        rollupsDeployCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.AUTOMATIONS_DELETE_COMPLETED:
      case WEBSOCKET_MESSAGES.ROLLUPS_DELETE_COMPLETED: {
        automationsDeleteCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.DEVOPS_CENTER_DEPLOY_ELEMENTS_COMPLETED: {
        devOpsCenterElementsDeployCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.DEPLOYMENT_COMPLETED: {
        deploymentCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.PARSE_COMPLETED: {
        parseCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.HOMEPAGE_REFRESH: {
        refreshHomepageDataHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.DATA_INTEGRATION_TEAMS_CONNECTION_COMPLETED: {
        teamsConnectionCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.DATA_INTEGRATION_TEAMS_DISCONNECTION_COMPLETED: {
        teamsDisconnectionCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.FIELDS_UTILIZATION_COMPLETED: {
        fieldsUtilizationCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.SCAN_PROGRESS: {
        issuesScanUpdateHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.AGENT_FILE_STATUS_UPDATED: {
        agentFileStatusUpdatedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.AI_CHAT_NAME_UPDATED: {
        chatNameUpdatedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.AI_RESEARCH_STARTED: {
        researchStartedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.AI_RESEARCH_PROGRESS: {
        researchProgressHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.AI_RESEARCH_COMPLETED: {
        researchCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.SIMULATION_COMPLETED: {
        simulationCompletedHandler(payload);
        break;
      }
      case WEBSOCKET_MESSAGES.TEAMS_INSTALL_SUCCESS: {
        teamsInstallCompletedHandler(payload, true);
        break;
      }
      case WEBSOCKET_MESSAGES.TEAMS_INSTALL_FAILURE: {
        teamsInstallCompletedHandler(payload, false);
        break;
      }
      case WEBSOCKET_MESSAGES.TEAMS_UNINSTALL_SUCCESS: {
        teamsUninstallCompletedHandler(payload, true);
        break;
      }
      case WEBSOCKET_MESSAGES.TEAMS_UNINSTALL_FAILURE: {
        teamsUninstallCompletedHandler(payload, false);
        break;
      }
      case WEBSOCKET_MESSAGES.AI_PROCESS_MINING_FUNNEL_MAP_COMPLETED: {
        aiProcessMiningFunnelMapCompletedHandler();
        break;
      }
      case WEBSOCKET_MESSAGES.AI_PROCESS_MINING_AGENT_FUNNEL_MAP_COMPLETED: {
        aiProcessMiningAgentFunnelMapCompletedHandler(payload);
        break;
      }
      default: {
        console.warn(
          `No dedicated handler was found for the emitted message of type: ${msgName}, going to dispatch a redux action `,
        );
        dispatch({
          type: msgName,
          payload,
        });
      }
    }
  };

  const getSocketUrl = useCallback(async () => {
    const authToken = await getAccessTokenSilently();
    return `${WEBSOCKET_ENDPOINT}?auth=${authToken}`;
  }, [getAccessTokenSilently]);

  const options: Options = {
    onOpen: () => {
      console.info('connected to websocket');
    },
    onClose: () => {
      console.info('disconnected from websocket');
    },
    onError: (event) => {
      console.error('WebSocket error', event);
      telemetry.captureError(new Error('WebSocket error'), {
        event,
      });
    },
    onMessage,
    shouldReconnect: () => true,
    heartbeat: {
      message: JSON.stringify({ action: 'ping', data: 'ping' }),
      returnMessage: 'pong',
      timeout: 60000, // 1 minute, if no response is received, the connection will be closed
      interval: 25000, // every 25 seconds, a ping message will be sent
    },
    reconnectInterval: 1000,
    reconnectAttempts: 10,
  };

  useWebSocket(getSocketUrl, options, true);

  // const connectionStatus = {
  //   [ReadyState.CONNECTING]: 'Connecting',
  //   [ReadyState.OPEN]: 'Open',
  //   [ReadyState.CLOSING]: 'Closing',
  //   [ReadyState.CLOSED]: 'Closed',
  //   [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  // }[readyState];
};

export default useWebSockets;
